import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import {
  useGetProfile as useGetProfileQuery,
  type ReceiverProfile,
  type AppError,
  type ProfileAddress,
  type Address,
  type CompletedInteraction,
  type useGetProfileHook,
  type ProfileEmailAddress,
} from '@dhl/receiver-profile-api'

const staleTime = 5 * 60 * 1000

export const useGetProfile = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProfileHook>>>,
  TError = AppError,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProfileHook>>>, TError, TData>>
}): ReturnType<typeof useGetProfileQuery<TData, TError>> =>
  useGetProfileQuery<TData, TError>({
    query: {
      ...options?.query,
      staleTime: options?.query?.staleTime ?? staleTime,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
  })

export const useGetProfileInitials = (): UseQueryResult<string, AppError> =>
  useGetProfileQuery<string>({
    query: {
      staleTime,
      select: (data) => [data?.firstName ?? '', data?.lastName ?? ''].map((n) => n[0]?.toUpperCase()).join(''),
    },
  })

export const useGetProfileFirstname = (): UseQueryResult<string, AppError> =>
  useGetProfileQuery<string>({
    query: {
      staleTime,
      select: (data) => data?.firstName ?? '',
    },
  })

export const useGetProfileAddressById = (addressId: string): UseQueryResult<ProfileAddress | undefined, AppError> =>
  useGetProfileQuery<ProfileAddress | undefined>({
    query: {
      staleTime,
      select: (data) => data.addresses?.find((address) => address.addressId === addressId),
    },
  })

export const useGetProfileLastAddedAddress = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProfileHook>>>,
  TError = AppError,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProfileHook>>>, TError, TData>>
}): UseQueryResult<ProfileAddress | undefined, AppError> =>
  useGetProfileQuery<ProfileAddress | undefined>({
    query: {
      // @ts-ignore
      staleTime: options?.query?.staleTime ?? staleTime,
      select: (data) => [...(data.addresses ?? [])].pop(),
    },
  })

export const useGetProfileEmailAddressById = (
  emailId: string
): UseQueryResult<ProfileEmailAddress | undefined, AppError> =>
  useGetProfileQuery<ProfileEmailAddress | undefined>({
    query: {
      staleTime,
      select: (data) => data.emailAddresses?.find((emailAddress) => emailAddress.emailId === emailId),
    },
  })

export const useGetProfileEmail = (): UseQueryResult<string | undefined, AppError> =>
  useGetProfileQuery<string | undefined>({
    query: {
      staleTime,
      select: (data) => data.email,
    },
  })

export const profileAddressByAddressSelector = (
  profile?: ReceiverProfile,
  dest?: Partial<Address>
): ProfileAddress | undefined =>
  profile?.addresses?.find(
    (a) =>
      a.address.postalCode.replace(/\s/g, '') ===
        (dest?.postalCode ? dest?.postalCode.replace(/\s/g, '') : dest?.postalCode) &&
      a.address.houseNumber === dest?.houseNumber &&
      a.address.houseNumberSuffix.toLowerCase() === dest?.houseNumberSuffix?.toLowerCase()
  )

export const useGetProfileAddressByAddress = (
  dest?: Partial<Address>
): UseQueryResult<ProfileAddress | undefined, AppError> =>
  useGetProfileQuery<ProfileAddress | undefined>({
    query: {
      staleTime,
      select: (data) => profileAddressByAddressSelector(data, dest),
    },
  })

export const useGetProfileCompletedInteractions = (): UseQueryResult<CompletedInteraction[], AppError> =>
  useGetProfileQuery<CompletedInteraction[]>({
    query: {
      staleTime,
      select: (data) => data.completedInteractions ?? [],
    },
  })

export const useIsProfilePrimaryEmailAnonymous = (): UseQueryResult<boolean, AppError> =>
  useGetProfileQuery<boolean>({
    query: {
      staleTime,
      select: (data) => data.email.endsWith('@privaterelay.appleid.com'),
    },
  })
